import { ThemeService } from './../../../core/services/theme.service';
import { Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, AfterViewInit, Renderer2 } from '@angular/core';
import { isStateModeGroup } from '../../util/state-mode.util';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import { EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { switchMap, takeUntil, startWith, debounceTime } from 'rxjs/operators';
import { TITLES } from './../../../core/data/app.data';
import { AppDataService, ProcedureCode } from './../../../core/services/app-data.service';
import { AssistantOption } from './../../../core/services/billing.service';
import { AssistantDetails, CodePromptingService, TimeDetails } from './../../../core/services/code-prompting.service';
import { ContactService } from './../../../core/services/contact.service';
import { DisciplineConfigService } from './../../../core/services/discipline-config.service';
import { FeedbackService } from './../../../core/services/feedback.service';
import { UserService } from './../../../core/services/user.service';
import { AppointmentTypeService } from 'src/app/core/services/appointment-type.service';
import { GroupService } from 'src/app/core/services/group.service';
import { CdkDragDrop, CdkDragEnd, CdkDragStart, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatChipGrid } from '@angular/material/chips';

@Component({
  selector: 'app-procedure-code-selector',
  templateUrl: './procedure-code-selector.component.html',
  styleUrls: ['./procedure-code-selector.component.scss']
})
export class ProcedureCodeSelectorComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  protected destroy$ = new Subject<void>();
  @Input() patient: any = {};
  @Input() providedStartTime: string = '';
  @Input() providedFinishTime: string = '';
  @Input() initialCodes: ProcedureCode[] = [];
  @Input() hintText: string = '';
  @Input() onChipDblClick: Function = () => { };
  @Input() modifiersOnly: boolean = false;
  @Input() modifierTypes: ('Reduce' | 'Add' | 'Info' | 'Compound')[] = [];
  @Input() pluralTitle: string = 'Procedure Codes';
  @Input() singularTitle: string = 'Procedure Code';
  @Input() collectRandValue: boolean = false;
  @Input() handleEditInternal: boolean = false;
  @Input() disableAdding: boolean = false;
  @Input() enableDragAndDrop: boolean = false;
  @Output() codesChanged = new EventEmitter<ProcedureCode[]>();
  @Output() codeDeleted = new EventEmitter<ProcedureCode>();

  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;
  @ViewChild('chipGrid', { read: ElementRef }) chipGridElement?: ElementRef<HTMLElement>;

  @ViewChild('priceOverrideDialog') priceOverrideDialog?: TemplateRef<any>;
  @ViewChild('codeBMIDialog') codeBMIDialog?: TemplateRef<any>;
  @ViewChild('codeTimeDialog') codeTimeDialog?: TemplateRef<any>;
  @ViewChild('codeAssistantDialog') codeAssistantDialog?: TemplateRef<any>;

  pcControl = new FormControl();
  filteredProcedureCodes: Observable<ProcedureCode[]>;
  existingCodes = new Set<string>();
  pcChips: ProcedureCode[] = [];
  readonly separatorKeysCodes: number[] = [13, 188]; // Enter and comma key codes
  addOnBlur = true;

  codeAdditionalInfo: any = {};
  assistantAdditionalInfo: AssistantDetails = { assistantName: '', assistantPracticeNumber: '' };
  previousAssistants: AssistantOption[] = [];
  filteredAssistants: AssistantOption[] = [];
  disciplineCode: string = 'medical';
  appointmentTypes: any[] = [];

  private themeSub?: Subscription;
  darkTheme!: NgxMaterialTimepickerTheme;

  titles = TITLES;
  contactMessage: any = {};
  @ViewChild('contactDialog') contactDialog?: TemplateRef<any>;
  group!: any;

  /** A state unit (tasks/state-mode.md) records ICD-10 codes only: every procedure code picker is hidden. */
  @HostBinding('hidden')
  get hiddenInStateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private appDataService: AppDataService,
    private dialog: MatDialog,
    private userService: UserService,
    private groupService: GroupService,
    private contactService: ContactService,
    private feedbackService: FeedbackService,
    private themeService: ThemeService,
    private appointmentTypeService: AppointmentTypeService,
    private codePromptingService: CodePromptingService,
    private disciplineConfigService: DisciplineConfigService,
    private renderer: Renderer2

  ) {
    this.filteredProcedureCodes = this.pcControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(''),
        debounceTime(300), // Add debounceTime for performance
        switchMap(value => this.filterProcedureCodes(value))
      );

    this.appointmentTypeService.appointmentTypes$
      .pipe(
        switchMap(appointmentTypes => {
          if (appointmentTypes) {
            this.appointmentTypes = appointmentTypes;
            return EMPTY;
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();
  }

  ngOnInit(): void {
    this.updateChips();

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(group => {
        if (group) {
          this.group = group;
          this.disciplineCode = this.disciplineConfigService.getDisciplineCodeForSpeciality(group.groupSpeciality);
        }
      });

    this.themeSub = this.themeService.isDarkMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDarkMode) => {
        this.darkTheme = this.themeService.getTimepickerTheme(isDarkMode);
      });
  }

  ngAfterViewInit(): void {
    if (!this.chipGridElement) {
      console.warn('ChipGrid element not available in ngAfterViewInit');
    }

    if (this.disableAdding) {
      this.pcControl.disable();
    }
    else {
      this.pcControl.enable();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialCodes']) {
      this.updateChips();
    }
    if (changes['patient']) {
      this.patient = changes['patient'].currentValue;
    }
    if (changes['providedStartTime']) {
      this.providedStartTime = changes['providedStartTime'].currentValue;
    }
    if (changes['providedFinishTime']) {
      this.providedFinishTime = changes['providedFinishTime'].currentValue;
    }
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }


  private updateChips(): void {
    // Initialize the chips with any initial codes passed in
    if (this.initialCodes) {
      this.pcChips = this.initialCodes.slice();

      // Ensure all codes have proper displayIndex values
      this.pcChips.forEach((code, index) => {
        if (code.displayIndex === undefined || code.displayIndex === null) {
          code.displayIndex = index;
        }
      });

      // Sort by displayIndex to maintain order
      this.pcChips.sort((a, b) => (a.displayIndex || 0) - (b.displayIndex || 0));
    }
  }

  private filterProcedureCodes(value: string): Observable<ProcedureCode[]> {
    return this.appDataService.searchCodes(value, 'pc', this.modifiersOnly, this.modifierTypes, this.group?.groupSpeciality).pipe(
      takeUntil(this.destroy$)
    );
  }

  resetInput(): void {
    setTimeout(() => {
      this.inputElement.nativeElement.value = '';
      this.pcControl.reset();
    });
  }

  truncateDescription(description: string): string {
    const length = description.length;
    if (length < 30) {
      return description;
    }

    return `${description.substring(0, 30)}...`
  }

  getTooltipText(code: any): string {
    let text = '';

    if (code.paidCash) {
      text += `(Paid Up Front) - `;
    }

    if (code.noCharge) {
      text += `(Pro Bono) - `;
    }

    if (code.BMI) {
      text += `BMI: ${code.BMI} - (Weight:${code.weight}, Height:${code.height}) - `;
    }

    if (code.startTime && code.endTime) {
      text += `(Time: ${code.startTime} - ${code.endTime}) - `;
    }

    if (code.assistantName) {
      const practiceNumber = code.assistantPracticeNumber ? ` - ${code.assistantPracticeNumber}` : '';
      text += `(Assistant: ${code.assistantName}${practiceNumber}) - `;
    }

    if (code.appointmentTypeId) {
      text += `(${this.appointmentTypes.find(at => at.id === code.appointmentTypeId)?.type}) - `;
    }

    if (code.priceOverride) {
      text += `(R ${code.priceOverride.toFixed(2)}) - `;
    }

    text += `${code.code} - ${code.description}`;
    return text;
  }

  async addChip(code: ProcedureCode): Promise<void> {
    const codeData = { code: code.code, description: code.description, units: code.units, type: code.type, tags: code.tags, startTime: '', endTime: '', assistantName: '', assistantPracticeNumber: '', weight: '', height: '', BMI: '', paidCash: false, noCharge: false, priceOverride: 0, appointmentTypeId: '', overrideDescription: '' };

    if (this.modifiersOnly) {
      this.saveChipDetails(codeData);
      return;
    }

    if (this.codePromptingService.shouldShowTimeFields(code.code.toString())) {
      const startEndDetails = await this.openStartFinishDialog();
      if (!startEndDetails) {
        return;
      }
      codeData.startTime = startEndDetails.startTime;
      codeData.endTime = startEndDetails.endTime;
    }

    // Cancelling only skips the name — the code itself is still added.
    if (this.codePromptingService.shouldShowAssistantFields(code.code.toString(), this.disciplineCode)) {
      const assistantDetails = await this.openAssistantDialog();
      codeData.assistantName = assistantDetails?.assistantName || '';
      codeData.assistantPracticeNumber = assistantDetails?.assistantPracticeNumber || '';
    }

    if (code.code.toString() === '0018') {
      const bmiDetails = await this.openBMIDialog({ weight: '', height: '', BMI: '' });
      codeData.weight = bmiDetails.weight;
      codeData.height = bmiDetails.height;
      codeData.BMI = bmiDetails.BMI;
    }

    if (this.collectRandValue) {
      const priceOverrideDetails = await this.openPriceOverrideDialog();
      codeData.priceOverride = priceOverrideDetails.priceOverride;
      codeData.appointmentTypeId = priceOverrideDetails.appointmentTypeId;
      codeData.overrideDescription = priceOverrideDetails.overrideDescription;
    }

    this.saveChipDetails(codeData);
  }

  private async openPriceOverrideDialog(amount?: number, appointmentTypeId?: string, overrideDescription?: string): Promise<any> {
    this.codeAdditionalInfo = { priceOverride: amount || 0, appointmentTypeId: appointmentTypeId || '', overrideDescription: overrideDescription || '' };
    const ref = this.dialog.open(this.priceOverrideDialog!, { autoFocus: false });
    const results = await ref.afterClosed().toPromise()
    if (results) {
      return this.codeAdditionalInfo;
    }
  }

  private async openStartFinishDialog(): Promise<TimeDetails | null> {
    const seed = { startTime: this.providedStartTime, endTime: this.providedFinishTime };
    this.codeAdditionalInfo = { ...seed };

    return this.codePromptingService.openStartFinishDialog(this.codeTimeDialog!, seed);
  }

  private async openAssistantDialog(assistantName?: string, assistantPracticeNumber?: string): Promise<AssistantDetails | null> {
    const seed = { assistantName: assistantName || '', assistantPracticeNumber: assistantPracticeNumber || '' };
    this.assistantAdditionalInfo = { ...seed };
    this.previousAssistants = await this.codePromptingService.getPreviousAssistants(this.group?.id);
    this.filterAssistants(seed.assistantName);

    return this.codePromptingService.openAssistantDialog(this.codeAssistantDialog!, seed);
  }

  filterAssistants(value: string): void {
    const filterValue = (value || '').toLowerCase();
    this.filteredAssistants = this.previousAssistants.filter(assistant => assistant.name.toLowerCase().includes(filterValue));
  }

  onAssistantSelected(name: string): void {
    const selected = this.previousAssistants.find(assistant => assistant.name === name);
    if (selected?.practiceNumber) {
      this.assistantAdditionalInfo.assistantPracticeNumber = selected.practiceNumber;
    }
  }

  private async openBMIDialog({ weight, height, BMI }: { weight: string, height: string, BMI: string }): Promise<any> {
    this.codeAdditionalInfo = { weight: weight || this.patient?.weight || '', height: height || this.patient.height || '', BMI: BMI || '' };
    this.calculateBMI();
    const ref = this.dialog.open(this.codeBMIDialog!, { autoFocus: false, disableClose: true });
    const results = await ref.afterClosed().toPromise()
    if (results) {
      return this.codeAdditionalInfo;
    }
    else {
      return null;
    }
  }

  calculateBMI() {
    const weight = this.codeAdditionalInfo.weight;
    const height = this.codeAdditionalInfo.height / 100; // Convert height from cm to meters

    if (weight && height) {
      this.codeAdditionalInfo.BMI = (weight / (height * height)).toFixed(2).toString();
    } else {
      this.codeAdditionalInfo.BMI = null;
    }
  }

  private saveChipDetails(codeData: any) {
    // Assign displayIndex based on current position
    codeData.displayIndex = this.pcChips.length;
    this.pcChips.push(codeData);
    this.codesChanged.emit(this.pcChips);
    this.resetInput();
  }

  /*  removeChip(index: number, pc: any): void {
     console.log('removeChip called with index:', index, 'and code:', pc);
     
     // Store the code before removing it
     const deletedCode = { ...pc };
     
     this.pcChips.splice(index, 1);
 
      // Always emit the deleted code, regardless of whether it has an ID
     // This ensures the parent component can handle the deletion properly
     console.log('Emitting codeDeleted event with:', deletedCode);
     this.codeDeleted.emit(deletedCode);
 
     
     // Reindex remaining codes to maintain proper displayIndex values
     this.pcChips.forEach((code, newIndex) => {
       code.displayIndex = newIndex;
     });
     
     // Emit the updated codes
     //this.codesChanged.emit(this.pcChips);
    
   } */

  removeChip(index: number, pc: any): void {
    this.pcChips.splice(index, 1);
    this.codesChanged.emit(this.pcChips);

    if (typeof pc.id === 'string') {
      this.codeDeleted.emit(pc);

    }
  }

  editChip(chip: ProcedureCode, event: any): void {
    const index = this.pcChips.findIndex(c => c.code === chip.code);
    if (index !== -1) {
      this.pcChips[index].description = event.value;
      this.codesChanged.emit(this.pcChips);
    }
  }

  async handleDblClick(chip: ProcedureCode, index: number): Promise<void> {

    if (this.handleEditInternal) {
      // Use the provided index instead of searching for it
      if (index < 0 || index >= this.pcChips.length) {
        console.warn('Invalid chip index provided:', index);
        return;
      }

      if (chip.priceOverride) {
        const priceOverrideDetails = await this.openPriceOverrideDialog(Number(chip.priceOverride), chip.appointmentTypeId, chip.overrideDescription);
        chip.priceOverride = priceOverrideDetails.priceOverride;
        chip.appointmentTypeId = priceOverrideDetails.appointmentTypeId;
        chip.overrideDescription = priceOverrideDetails.overrideDescription;
      }

      if (this.codePromptingService.shouldShowTimeFields(chip.code.toString())) {
        this.providedStartTime = chip.startTime ?? '';
        this.providedFinishTime = chip.endTime ?? '';
        const startEndDetails = await this.openStartFinishDialog();
        if (!startEndDetails) {
          return;
        }
        chip.startTime = startEndDetails.startTime;
        chip.endTime = startEndDetails.endTime;
      }

      if (this.codePromptingService.shouldShowAssistantFields(chip.code.toString(), this.disciplineCode)) {
        const assistantDetails = await this.openAssistantDialog(chip.assistantName, chip.assistantPracticeNumber);
        chip.assistantName = assistantDetails?.assistantName ?? chip.assistantName;
        chip.assistantPracticeNumber = assistantDetails?.assistantPracticeNumber ?? chip.assistantPracticeNumber;
      }

      if (chip.code.toString() === '0018') {
        const bmiDetails = await this.openBMIDialog({ weight: chip.weight || '', height: chip.height || '', BMI: chip.BMI || '' });
        chip.weight = bmiDetails.weight;
        chip.height = bmiDetails.height;
        chip.BMI = bmiDetails.BMI;
      }

      this.pcChips[index] = chip;
      this.codesChanged.emit(this.pcChips);

    }

    if (this.onChipDblClick) {
      this.onChipDblClick(chip);
    }
  }

  openContactUsDialog(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    const user = this.userService.user;
    this.contactMessage = { title: user?.title || '', name: user?.name || '', surname: user?.surname || '', email: user?.email || '', phone: '', message: '' };
    this.dialog.open(this.contactDialog!, { width: '600px' });
  }

  sendContactMessage(): void {

    const sub = this.contactService.sendContactEmail(this.contactMessage)
      .subscribe(
        (response: any) => {
          this.feedbackService.showToast('Your contact request has been sent successfully.');
          sub.unsubscribe();
        },
        (error) => {
          this.feedbackService.showToast('There was an error submitting your contact request. Please try again.');
          sub.unsubscribe();
        }
      );
    this.dialog.closeAll(); // Close the edit colleague dialog
  }

  /**
   * Handles drag and drop reordering of procedure codes
   * Updates displayIndex values to maintain order
   */
  onDrop(event: CdkDragDrop<ProcedureCode[]>): void {
    if (!this.enableDragAndDrop) return;

    // Move the item in the array
    moveItemInArray(this.pcChips, event.previousIndex, event.currentIndex);

    // Update displayIndex values to reflect new order
    this.pcChips.forEach((code, index) => {
      code.displayIndex = index;
    });

    // Emit the updated codes
    this.codesChanged.emit(this.pcChips);
  }

  /**
   * Handles drag start event to add vertical stacking class
   */
  onDragStarted(): void {
    const el = this.chipGridElement?.nativeElement;
    console.log('Drag started, chipGridElement available:', !!el);
    if (el) {
      this.renderer.addClass(el, 'dragging');
      console.log('Added dragging class to chipGrid');

      // Also find the chip set within this specific component
      const chipSet = el.querySelector('.mdc-evolution-chip-set__chips');
      if (chipSet) {
        this.renderer.addClass(chipSet, 'dragging');
        console.log('Added dragging class to chipSet');
      } else {
        console.log('ChipSet not found within chipGrid');
      }
    } else {
      console.warn('chipGridElement not available - ViewChild not properly initialized');
    }
  }

  /**
   * Handles drag ended event to remove vertical stacking class
   */
  onDragEnded(): void {
    const el = this.chipGridElement?.nativeElement;
    console.log('Drag ended, chipGridElement available:', !!el);
    if (el) {
      this.renderer.removeClass(el, 'dragging');
      console.log('Removed dragging class from chipGrid');

      // Also find the chip set within this specific component
      const chipSet = el.querySelector('.mdc-evolution-chip-set__chips');
      if (chipSet) {
        this.renderer.removeClass(chipSet, 'dragging');
        console.log('Removed dragging class from chipSet');
      } else {
        console.log('ChipSet not found within chipGrid');
      }
    } else {
      console.warn('chipGridElement not available - ViewChild not properly initialized');
    }
  }

  /**
   * Gets the drag handle icon based on theme
   */
  getDragHandleIcon(): string {
    return 'drag_indicator';
  }

  /**
   * Checks if drag and drop is enabled and there are multiple codes
   */
  /*  get isDragAndDropEnabled(): boolean {
     return this.enableDragAndDrop && this.pcChips.length > 1;
   } */
}